import React, { memo, useContext, useEffect, useRef, Fragment, type ContextType } from 'react';

import { ContextPanel } from '@atlaskit/editor-core';

import {
	useIsObjectSidebarShown,
	usePanelWidth,
	usePanel,
	MIN_PANEL_WIDTH,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { isMigratedToPanelSystem } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { ApprovalsPane } from '@confluence/approvals/entry-points/ApprovalsPane.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_PANE_ID,
	APPROVALS_PANE_WIDTH,
	getApprovalsPaneContextKey,
	shouldRenderApprovalsPaneForContext,
	syncApprovalsPaneContextOwnership,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

export const EditorContextPanelView = memo(() => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const panelWidth = usePanelWidth();
	const panel = usePanel();

	const { item: contextPanelItem, editorAPI } = useContext(
		EditorContextPanelContext,
	) as ContextType<typeof EditorContextPanelContext>;

	const { contentId, contentStatus } = useApprovals();
	const rightSidebarContext = useRightSidebarContext();
	const isSidebarApprovalsPaneOpen =
		rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID;
	const approvalsPaneContextKey = getApprovalsPaneContextKey({ contentId, contentStatus });
	const isApprovalsPaneOpen = shouldRenderApprovalsPaneForContext({
		isApprovalsExperimentEnabled: expValEquals('confluence_approvals_eap', 'isEnabled', true),
		isSidebarApprovalsPaneOpen,
		contextKey: approvalsPaneContextKey,
	});

	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	useEffect(() => {
		syncApprovalsPaneContextOwnership({
			isApprovalsExperimentEnabled: expValEquals('confluence_approvals_eap', 'isEnabled', true),
			isSidebarApprovalsPaneOpen,
			contextKey: approvalsPaneContextKey,
			closeApprovalsPane: () => rightSidebarContextRef.current.close(APPROVALS_PANE_ID),
		});
	}, [approvalsPaneContextKey, isSidebarApprovalsPaneOpen]);

	if (isApprovalsPaneOpen) {
		return (
			<ContextPanel
				visible
				editorAPI={editorAPI ?? undefined}
				hasPadding={false}
				customWidth={APPROVALS_PANE_WIDTH}
			>
				<ApprovalsPane />
			</ContextPanel>
		);
	}

	// Previously, the pattern was to set state in EditorContextPanelContext in order to render components here.
	// Now, we render components directly in EditorContextPanelView to avoid bugs like https://hello.jira.atlassian.cloud/browse/DISCO-4183
	//
	// When the panel system flag is on, comments/details/jira panels render via navigation-system Panel in
	// PageLayout.tsx. Extension panels (ToC, etc.) always render here in ContextPanel.
	if (
		isObjectSidebarShown &&
		(fg('confluence_frontend_content_wrapper') || isRovoButtonEnabled) &&
		!(fg('ads-panel-system-confluence-object-sidebar') && isMigratedToPanelSystem(panel?.id))
	) {
		return (
			<ContextPanel
				visible
				editorAPI={editorAPI ?? undefined}
				hasPadding={false}
				customWidth={
					(panel?.id === 'proactive-ai-panel' ||
						(panel?.id.startsWith && panel?.id.startsWith('extensions-panel')) ||
						panel?.id === 'editor-element-insert-sidebar-panel') &&
					fg('platform_editor_ai_object_sidebar_injection')
						? panelWidth || MIN_PANEL_WIDTH
						: MIN_PANEL_WIDTH
				}
			>
				<ObjectSidebarPanel />
			</ContextPanel>
		);
	}

	let component;
	let hasPadding;
	let customWidth;

	if (contextPanelItem) {
		component = contextPanelItem.component;
		hasPadding = contextPanelItem.hasPadding;
		customWidth = contextPanelItem.customWidth;
	}

	return (
		<ContextPanel
			visible={!!contextPanelItem}
			editorAPI={editorAPI ?? undefined}
			hasPadding={hasPadding}
			customWidth={customWidth}
		>
			<Fragment>{component}</Fragment>
		</ContextPanel>
	);
});
